"use client";

import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef } from "react";
import { getMetaPageContent, META_PIXEL_ID } from "@/lib/meta/config";
import { persistFacebookClickId, readMetaLead, trackMetaEvent, trackMetaEventOnce } from "@/lib/meta/browser";

function MetaEventTracker() {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const lastKey = useRef("");

    useEffect(() => {
        persistFacebookClickId();
        const key = `${pathname}?${searchParams.toString()}`;
        if (lastKey.current === key) return;
        lastKey.current = key;

        const page = getMetaPageContent(pathname);
        const lead = readMetaLead();
        const pageParams = page
            ? { content_name: page.contentName, content_category: page.contentCategory }
            : { content_name: pathname, content_category: "site" };

        trackMetaEvent("PageView", lead, pageParams);

        if (page?.event && page.event !== "PageView") {
            trackMetaEventOnce(`${page.event}:${pathname}`, page.event, lead, pageParams);
        }
    }, [pathname, searchParams]);

    useEffect(() => {
        const onClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            const link = target.closest("a[href*='wa.me'], a[href*='api.whatsapp.com']");
            if (!link) return;
            if (pathname.replace(/\/$/, "") === "/lpmoov2/obrigado") return;
            trackMetaEvent("Contact", readMetaLead(), {
                content_name: "WhatsApp",
                content_category: "contact",
            });
        };

        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, [pathname]);

    return null;
}

export default function MetaPixel() {
    return (
        <>
            <Script id="meta-pixel-stub" strategy="beforeInteractive">
                {`
                  !function(f,n){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];}(window);
                  fbq('init', '${META_PIXEL_ID}');
                `}
            </Script>
            <Script
                id="meta-pixel"
                src="https://connect.facebook.net/en_US/fbevents.js"
                strategy="afterInteractive"
            />
            <noscript>
                <img
                    height="1"
                    width="1"
                    style={{ display: "none" }}
                    src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
                    alt=""
                />
            </noscript>
            <Suspense fallback={null}>
                <MetaEventTracker />
            </Suspense>
        </>
    );
}
